import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { format } from 'date-fns'
import { useDimensions, Typography as Typo, Card } from '@telavita-core/react-design-kit'
import { useAllPrismicDocumentsByType } from '@prismicio/react'

import ProfessionalsService from 'services/professionals'
import { fetchNextSchedules } from 'services/me'

import { Typography, CoreIcons, Spinner, ProfessionalContentHighlights } from 'components'
import * as routes from '../../../../settings/_routesSettings'
import Row from 'components/Row'
import Column from 'components/Column'
import { TalkToUsCard } from './components/TalkToUsCard'

import BallonCommentIcon from 'components/CoreIcons/Icons/BallonComment'

import { ReactComponent as MediaTestIcon } from 'static/svg/test-media-icon.svg'

const professionalContentByRole = {
  PSIQ: 'psiq_professional_dashboard_content',
  PSIC: 'professional_dashboard_content'
}

const ProfessionalHome = () => {
  const history = useHistory()
  const { isMobile } = useDimensions()
  const [schedules, setSchedules] = useState([])
  const [loading, setLoading] = useState(false)
  const [reviewsNotViewed, setReviewsNotViewed] = useState(null)

  const firstName = useSelector(state => state.auth.userData.person_general.first_name)
  const professionalRole = useSelector(state => state.auth.userData.session.profile_role_active)

  const [documents, { state }] = useAllPrismicDocumentsByType(professionalContentByRole[professionalRole])

  const isLoadingContents = state === 'loading'

  const professionalContents = documents ? documents.map(doc => ({
    title: doc.data.title[0].text,
    description: doc.data.description[0].text,
    url: doc.data.link.url,
    order: doc.data.order,
    image: {
      url: doc.data.image.url,
      alt: doc.data.image.alt
    },
  })).sort((a,b) => a.order-b.order) : []

  useEffect(() => {
    setLoading(true)

    fetchNextSchedules()
      .then(response => {
        setSchedules(response)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    async function getNotViewedReviews() {
      const response = await ProfessionalsService.reviewsNotViewedCount()

      setReviewsNotViewed(response)
    }

    getNotViewedReviews()
  }, [])

  const renderScheduleItem = (schedule) => {
    const date = format(new Date(schedule.startDate), 'dd/MM/yyyy')
    const time = format(new Date(schedule.startDate), 'HH:mm')

    return (
      <div className="ProfessionalHome__ScheduleItem" key={schedule.id}>
        <Typography bold>{schedule?.patientFullName}</Typography>
        <div className="ProfessionalHome__ScheduleItem__Grid">
          <CoreIcons
            name="CalendarOneDay"
            fill="#1f35b5"
            height="24"
            viewBox="0 0 24 24"
            width="24"
          />
          <Typography>{date} - {time}</Typography>
        </div>
      </div>
    )
  }

  const renderSchedules = () => {
    if (loading)
      return <Spinner height="48" />

    return (
      schedules.length > 0 ?
        schedules.map((schedule, index) => index < 5 && renderScheduleItem(schedule))
        :
        <div className="ProfessionalHome__EmptySchedules">
          <CoreIcons
            fill="#c1c1c1"
            height="40"
            viewBox="0 0 40 40"
            width="40"
            name="Schedule"
          />
          <div style={{height: 10}}></div>
          <Typo variant='content3' inheritColor>
            Nenhuma consulta
          </Typo>
        </div>
    )
  }

  const renderReviewNotification = () => (
    <Card 
      customClassName='ProfessionalHome__Card'
      inlineStyles={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      {(reviewsNotViewed === null) && <Spinner height="48" />}
      {(reviewsNotViewed || reviewsNotViewed === 0) && (
        <>
          <Row
            maxWidth='260px'
          >
            <Typo
              variant='content1'
              weight='bolder'
            >
              Depoimentos de pacientes
            </Typo>
            <Typo
              variant='content3'
              inlineStyles={{
                marginTop: 5,
                color: '#898989',
                lineHeight: '16px',
              }}
            >
              Veja os comentários de quem te avaliou com notas 9 e 10 e publique-os em seu perfil
            </Typo>
            <Typography
              Tag='a'
              onTextClick={() => history.push(routes.DASHBOARD_REVIEWS_PROFESSIONAL)}
              style={{
                marginTop: '30px',
              }}
            >
              Ver depoimentos
            </Typography>
          </Row>
          <Row
            justify='flex-end'
            width='auto'
            position='relative'
            padding='0 15px 0 0'
          >
            <BallonCommentIcon
              fill='#1F35B5'
              width='52'
              height='52'
              viewBox='0 0 52 52'
            />
            {reviewsNotViewed > 0 && (
              <Typo
                variant='content2'
                customClassName='ProfessionalHome__Card__ReviewNotification'
                inlineStyles={{
                  color: '#fff',
                  padding: '2px 7px',
                }}
              >
                {reviewsNotViewed}
              </Typo>
            )}
          </Row>
        </>
      )}
    </Card>
  )

  const renderTestMeetRoomCard = () => (
    <Card 
      customClassName='ProfessionalHome__Card'
      inlineStyles={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Row maxWidth='260px'>
        <Typo variant='content1' weight='bolder'>
          Testar câmera e microfone
        </Typo>
        <Typo
          variant='content3'
          inlineStyles={{
            marginTop: 5,
            color: '#898989',
            lineHeight: '16px',
          }}
        >
          Assegure-se de que vai funcionar no seu dispositivo.
        </Typo>
        <Typography
          Tag='a'
          onTextClick={() => window.open(process.env.REACT_APP_MEET_TEST_URL, '_self')}
          style={{
            marginTop: '30px',
          }}
        >
          Fazer um teste
        </Typography>
      </Row>
      <MediaTestIcon />
    </Card>
  )

  const renderProfessionalContentHighlights = () => {
    if (documents && documents.length <= 0) return
    return (
      <ProfessionalContentHighlights 
        mainContent={professionalContents.find(post => post.order === '1')}
        contents={professionalContents.filter((_, index) => index !== 0)}
        isLoading={isLoadingContents}
      />  
    )
  }

  return (
    <div className="ProfessionalHome">
      <Row className="Dashboard__Header">
        <Typo variant={!isMobile ? 'header2' : 'header4'}>
            Bem-vinda(o) {firstName}
        </Typo>
      </Row>
      <Column colWidth={6}>
        <Card customClassName='ProfessionalHome__Card'>
          <Typo variant='content1' weight='bolder'>
              Próximas consultas agendadas
          </Typo>
          <div className="ProfessionalHome__Schedules">
            {renderSchedules()}
          </div>
          <Typography Tag='a' onTextClick={() => history.push(routes.DASHBOARD_AGENDA)}>
            Ver minha agenda
          </Typography>
        </Card>
        {!isMobile && (<TalkToUsCard />)}

        {isMobile && (
          <>
            {renderReviewNotification()}
            {renderProfessionalContentHighlights()}
            <TalkToUsCard />
            {renderTestMeetRoomCard()}
          </>
        )}
      </Column>

      <Column colWidth={6}>
        {!isMobile && (
            <>
              {renderReviewNotification()}
              {renderProfessionalContentHighlights()}
              {renderTestMeetRoomCard()}
            </>
        )}
      </Column>
    </div>
  )
}

export {
  ProfessionalHome
}
